import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import QuotationPDF from './QuotationPDF';

interface QuotationData {
  refNo: string;
  date: string;
  to: string;
  subject: string;
  yourRef: string;
  offerText: string;
  ourOffer: string;
  amountInWords: string;
  termsAndConditions: string[];
  closingText: string;
  salutation: string;
  signature: string;
}

interface QuotationService {
  srNo: string;
  description: string;
  quantity: string;
  rate: string;
  totalAmount: string;
}

interface EditableQuotationProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (quotationData: QuotationData, services: QuotationService[]) => Promise<void>;
  initialData?: QuotationData;
  initialServices?: QuotationService[];
}

const EditableQuotation: React.FC<EditableQuotationProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  initialServices
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [quotationData, setQuotationData] = useState<QuotationData>(initialData || {
    refNo: 'MTS/SEMPL/2025/07/01',
    date: '18.07.2025',
    to: 'Sai Engineers And Machineries Pvt Ltd.',
    subject: 'QUOTATION FOR RLA TESTING, BOILER 35TPH',
    yourRef: 'Your call enquiry No. 17.07.2025 (Ref. no: 178 & 203)',
    offerText: 'With reference to above we are pleased to offer our lowest competitive professional service charges for carrying out the inspection, examination, RLA',
    ourOffer: 'As requested by you, we are forwarding herewith the lowest competitive offer for above work with break up of scope of work as under: -',
    amountInWords: 'Five Lakh Fifty Thousand Only.',
    termsAndConditions: [
      '1. Written confirmation of order is essential & order can be finalized with mutual discussions',
      '2. Payment Terms :- 70% advance and Within 7days after completion of work',
      '3. GST: 18% Applicable. GST NO.27BARPM49209B1ZL HSN Code 998399',
      '4. Necessary Assistance:- To be provided by you as & when required.',
      '5. Liability :- We shall have limited liability up to 10% of our fees in case of any legal dispute/ hassles arises due to force major clause.',
      '6. Offer validity :- 30 days.',
      '7. Guest House/ Lodging & Boarding:- To be provided by Karkhana or extra at Actuals.',
      '8. Material must available before our engineer reach spot cleaning in your scope.'
    ],
    closingText: 'We hope that, this will be sufficient to meet your requirements & we shall assure you full cooperation at all times.',
    salutation: 'Thanking you,',
    signature: 'MALUNJKAR TECHNICAL SERVICES'
  });

  const [services, setServices] = useState<QuotationService[]>(initialServices || [
    { srNo: '1', description: 'NDT Services FOR RLA inspection for boiler size 35TPH', quantity: '01', rate: '5,50,000', totalAmount: '5,50,000 /-' }
  ]);

  const handleInputChange = (field: keyof QuotationData, value: string | string[]) => {
    setQuotationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceChange = (index: number, field: keyof QuotationService, value: string) => {
    setServices(prev => prev.map((service, i) => 
      i === index ? { ...service, [field]: value } : service
    ));
  };

  const addService = () => {
    const newService: QuotationService = {
      srNo: (services.length + 1).toString(),
      description: '',
      quantity: '',
      rate: '',
      totalAmount: ''
    };
    setServices(prev => [...prev, newService]);
  };

  const removeService = (index: number) => {
    if (services.length > 1) {
      setServices(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(quotationData, services);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving quotation:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const blob = await pdf(<QuotationPDF quotationData={quotationData} services={services} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quotation_${quotationData.refNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = () => {
    // Create a new window for printing only the quotation content
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Quotation Report - ${quotationData.refNo}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              font-size: 12px;
              line-height: 1.3;
            }
            .quotation-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
            }
            .header-image {
              width: 100%;
              max-height: 120px;
              object-fit: contain;
              margin-bottom: 10px;
            }
            .top-details {
              display: flex;
              justify-content: space-between;
              margin-bottom: 15px;
            }
            .ref-no { font-weight: bold; }
            .to-section { margin-bottom: 15px; }
            .to-label { font-weight: bold; }
            .to-name { font-weight: bold; margin-top: 5px; }
            .subject { margin-bottom: 15px; }
            .subject-label { font-weight: bold; }
            .subject-text { font-weight: bold; text-decoration: underline; }
            .reference { margin-bottom: 15px; }
            .reference-label { font-weight: bold; }
            .reference-text { font-style: italic; }
            .offer-text { margin-bottom: 15px; }
            .our-offer { margin-bottom: 20px; }
            .our-offer-label { font-weight: bold; }
            .services-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
            }
            .services-table th,
            .services-table td {
              border: 1px solid #000;
              padding: 8px;
              text-align: left;
            }
            .services-table th {
              background-color: #f5f5f5;
              font-weight: bold;
              text-align: center;
            }
            .services-table .sr-no { width: 8%; text-align: center; }
            .services-table .description { width: 52%; }
            .services-table .quantity { width: 12%; text-align: center; }
            .services-table .rate { width: 14%; text-align: right; }
            .services-table .total { width: 14%; text-align: right; }
            .amount-in-words {
              margin-bottom: 20px;
              font-weight: bold;
            }
            .amount-label { font-weight: bold; }
            .amount-text { font-weight: bold; text-decoration: underline; }
            .terms-section { margin-bottom: 20px; }
            .terms-title {
              font-weight: bold;
              text-decoration: underline;
              margin-bottom: 10px;
            }
            .terms-list { padding-left: 0; list-style: none; }
            .term-item {
              font-size: 10px;
              margin-bottom: 5px;
              line-height: 1.2;
            }
            .closing-text { margin-bottom: 15px; }
            .signature { margin-bottom: 15px; }
            .salutation { margin-bottom: 5px; }
            .signature-text { font-weight: bold; }
            @media print {
              body { margin: 0; padding: 0; }
              .quotation-container { max-width: none; }
            }
          </style>
        </head>
        <body>
          <div class="quotation-container">
            <!-- Header Section -->
            <div>
              <img src="${window.location.origin}/cpheader.png" alt="AVI'S ENGINEERING & CONSULTANCY" class="header-image" />
            </div>

            <!-- Top Details -->
            <div class="top-details">
              <div>
                <span class="ref-no">Ref No: ${quotationData.refNo}</span>
              </div>
              <div>
                <span>Date: ${quotationData.date}</span>
              </div>
            </div>

            <!-- To Section -->
            <div class="to-section">
              <div class="to-label">To,</div>
              <div class="to-name">${quotationData.to}</div>
            </div>

            <!-- Subject -->
            <div class="subject">
              <span class="subject-label">Subject: </span>
              <span class="subject-text">${quotationData.subject}</span>
            </div>

            <!-- Reference -->
            <div class="reference">
              <span class="reference-label">Reference: </span>
              <span class="reference-text">${quotationData.yourRef}</span>
            </div>

            <!-- Offer Text -->
            <div class="offer-text">
              <p>${quotationData.offerText}</p>
            </div>

            <!-- Our Offer -->
            <div class="our-offer">
              <span class="our-offer-label">A) OUR OFFER :- </span>
              <span>${quotationData.ourOffer}</span>
            </div>

            <!-- Services Table -->
            <table class="services-table">
              <thead>
                <tr>
                  <th class="sr-no">Sr No.</th>
                  <th class="description">Description</th>
                  <th class="quantity">Quantity</th>
                  <th class="rate">Rate</th>
                  <th class="total">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                ${services.map(service => `
                  <tr>
                    <td class="sr-no">${service.srNo}</td>
                    <td class="description">${service.description}</td>
                    <td class="quantity">${service.quantity}</td>
                    <td class="rate">${service.rate}</td>
                    <td class="total">${service.totalAmount}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <!-- Amount in Words -->
            <div class="amount-in-words">
              <span class="amount-label">Amount in Word:- </span>
              <span class="amount-text">${quotationData.amountInWords}</span>
            </div>

            <!-- Terms and Conditions -->
            <div class="terms-section">
              <div class="terms-title">TERMS/CONDITIONS:-</div>
              <ul class="terms-list">
                ${quotationData.termsAndConditions.map(term => `
                  <li class="term-item">${term}</li>
                `).join('')}
              </ul>
            </div>

            <!-- Closing Text -->
            <div class="closing-text">
              <p>${quotationData.closingText}</p>
            </div>

            <!-- Signature -->
            <div class="signature">
              <div class="salutation">${quotationData.salutation}</div>
              <div class="signature-text">${quotationData.signature}</div>
            </div>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto print:max-w-none print:max-h-none print:rounded-none print:shadow-none">
        <div className="p-6 print:p-0">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 print:hidden">
            <h2 className="text-2xl font-bold text-gray-800">Quotation Report</h2>
            <div className="flex space-x-2">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Edit Quotation
                  </button>
                  <button
                    onClick={handleDownloadPDF}
                    disabled={isDownloading}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {isDownloading ? 'Downloading...' : 'Download PDF'}
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Print
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>

          {/* Quotation Content */}
          <div className="flex justify-center p-4 bg-gray-100 min-h-screen font-sans text-sm print:bg-white print:p-0">
            <div className="w-full max-w-4xl bg-white border border-gray-300 shadow-lg p-6 print:shadow-none print:border-none">
              {/* Header Section */}
              <div className="w-full mb-2">
                <img src="/cpheader.png" alt="AVI'S ENGINEERING & CONSULTANCY" className="w-full max-h-32 object-contain" />
              </div>

              {/* Top Details */}
              <div className="mt-2 flex justify-between">
                <div className="flex-1">
                  <span className="font-bold mr-2">Ref No:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={quotationData.refNo}
                      onChange={(e) => handleInputChange('refNo', e.target.value)}
                      className="border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500"
                    />
                  ) : (
                    <span className="font-bold">{quotationData.refNo}</span>
                  )}
                </div>
                <div className="text-right">
                  <span>Date: </span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={quotationData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                      className="border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500"
                    />
                  ) : (
                    <span>{quotationData.date}</span>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <span className="font-bold">To,</span>
                <br />
                {isEditing ? (
                  <input
                    type="text"
                    value={quotationData.to}
                    onChange={(e) => handleInputChange('to', e.target.value)}
                    className="font-bold border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 w-full"
                  />
                ) : (
                  <span className="font-bold">{quotationData.to}</span>
                )}
              </div>

              <div className="mt-4">
                <span className="font-bold">Subject:</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={quotationData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="underline font-bold border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 ml-2 w-full"
                  />
                ) : (
                  <span className="underline font-bold ml-2">{quotationData.subject}</span>
                )}
              </div>

              <div className="mt-4">
                <span className="font-bold">Reference:</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={quotationData.yourRef}
                    onChange={(e) => handleInputChange('yourRef', e.target.value)}
                    className="italic border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 ml-2 w-full"
                  />
                ) : (
                  <span className="italic ml-2">{quotationData.yourRef}</span>
                )}
              </div>

              <div className="mt-4">
                {isEditing ? (
                  <textarea
                    value={quotationData.offerText}
                    onChange={(e) => handleInputChange('offerText', e.target.value)}
                    className="w-full border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 resize-none"
                    rows={2}
                  />
                ) : (
                  <p>{quotationData.offerText}</p>
                )}
              </div>

              <div className="mt-4">
                <span className="font-bold">A) OUR OFFER :-</span>
                {isEditing ? (
                  <textarea
                    value={quotationData.ourOffer}
                    onChange={(e) => handleInputChange('ourOffer', e.target.value)}
                    className="ml-2 border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 w-full resize-none"
                    rows={2}
                  />
                ) : (
                  <span className="ml-2">{quotationData.ourOffer}</span>
                )}
              </div>

              {/* Services Table */}
              <div className="mt-4">
                 <div className="flex justify-between items-center mb-2">
                   <span className="font-bold">Services:</span>
                   {isEditing && (
                     <button
                       onClick={addService}
                       className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 print:hidden"
                     >
                       Add Service
                     </button>
                   )}
                 </div>
                <table className="w-full border border-black border-collapse">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-2 border border-black">Sr No.</th>
                      <th className="p-2 border border-black">Description</th>
                      <th className="p-2 border border-black">Quantity</th>
                      <th className="p-2 border border-black">Rate</th>
                       <th className="p-2 border border-black">Total Amount</th>
                       {isEditing && <th className="p-2 border border-black print:hidden">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service, index) => (
                      <tr key={index}>
                        <td className="p-2 border border-black text-center">
                          {isEditing ? (
                            <input
                              type="text"
                              value={service.srNo}
                              onChange={(e) => handleServiceChange(index, 'srNo', e.target.value)}
                              className="w-full text-center border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500"
                            />
                          ) : (
                            service.srNo
                          )}
                        </td>
                        <td className="p-2 border border-black">
                          {isEditing ? (
                            <textarea
                              value={service.description}
                              onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                              className="w-full border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 resize-none"
                              rows={2}
                            />
                          ) : (
                            service.description
                          )}
                        </td>
                        <td className="p-2 border border-black text-center">
                          {isEditing ? (
                            <input
                              type="text"
                              value={service.quantity}
                              onChange={(e) => handleServiceChange(index, 'quantity', e.target.value)}
                              className="w-full text-center border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500"
                            />
                          ) : (
                            service.quantity
                          )}
                        </td>
                        <td className="p-2 border border-black text-right">
                          {isEditing ? (
                            <input
                              type="text"
                              value={service.rate}
                              onChange={(e) => handleServiceChange(index, 'rate', e.target.value)}
                              className="w-full text-right border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500"
                            />
                          ) : (
                            service.rate
                          )}
                        </td>
                        <td className="p-2 border border-black text-right">
                          {isEditing ? (
                            <input
                              type="text"
                              value={service.totalAmount}
                              onChange={(e) => handleServiceChange(index, 'totalAmount', e.target.value)}
                              className="w-full text-right border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500"
                            />
                          ) : (
                            service.totalAmount
                          )}
                        </td>
                         {isEditing && (
                           <td className="p-2 border border-black text-center print:hidden">
                             <button
                               onClick={() => removeService(index)}
                               disabled={services.length === 1}
                               className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                             >
                               Remove
                             </button>
                           </td>
                         )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 text-sm font-bold">
                <span>Amount in Word:-</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={quotationData.amountInWords}
                    onChange={(e) => handleInputChange('amountInWords', e.target.value)}
                    className="underline border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 ml-2 w-full"
                  />
                ) : (
                  <span className="underline ml-2">{quotationData.amountInWords}</span>
                )}
              </div>

              {/* Terms and Conditions */}
              <div className="mt-4">
                <span className="font-bold underline">TERMS/CONDITIONS:-</span>
                <div className="mt-2">
                  {isEditing ? (
                    <div className="space-y-2">
                      {quotationData.termsAndConditions.map((item, index) => (
                        <div key={index} className="flex items-start">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const updatedTerms = [...quotationData.termsAndConditions];
                              updatedTerms[index] = e.target.value;
                              handleInputChange('termsAndConditions', updatedTerms);
                            }}
                            className="flex-1 text-xs border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500"
                          />
                           <button
                             onClick={() => {
                               const updatedTerms = quotationData.termsAndConditions.filter((_, i) => i !== index);
                               handleInputChange('termsAndConditions', updatedTerms);
                             }}
                             className="ml-2 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 print:hidden"
                           >
                             Remove
                           </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const updatedTerms = [...quotationData.termsAndConditions, ''];
                          handleInputChange('termsAndConditions', updatedTerms);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 print:hidden"
                      >
                        Add Term
                      </button>
                    </div>
                  ) : (
                    <ul className="list-none p-0">
                      {quotationData.termsAndConditions.map((item, index) => (
                        <li key={index} className="mb-1 text-xs">{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Closing */}
              <div className="mt-4">
                {isEditing ? (
                  <textarea
                    value={quotationData.closingText}
                    onChange={(e) => handleInputChange('closingText', e.target.value)}
                    className="w-full border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 resize-none"
                    rows={2}
                  />
                ) : (
                  <p>{quotationData.closingText}</p>
                )}
              </div>

              <div className="mt-4">
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={quotationData.salutation}
                      onChange={(e) => handleInputChange('salutation', e.target.value)}
                      className="border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 w-full"
                    />
                    <br />
                    <input
                      type="text"
                      value={quotationData.signature}
                      onChange={(e) => handleInputChange('signature', e.target.value)}
                      className="font-bold border-b border-dotted border-gray-400 bg-transparent focus:outline-none focus:border-blue-500 w-full"
                    />
                  </div>
                ) : (
                  <>
                    <p>{quotationData.salutation}</p>
                    <br />
                    <p className="font-bold">{quotationData.signature}</p>
                  </>
                )}
              </div>

               {/* Watermark */}
               <div className="absolute bottom-20 right-8 text-gray-200 text-6xl font-bold transform rotate-90 opacity-10 pointer-events-none print:hidden">
                 AVIS
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditableQuotation;
